import React, { ReactNode } from 'react';

// Components
import { HomeIcon } from '@zola/zola-ui/src/components/SvgIconsV3/Home';
import { VenueIcon } from '@zola/zola-ui/src/components/SvgIconsV3/Venue';
import { ReportMoneyIcon } from '@zola/zola-ui/src/components/SvgIconsV3/ReportMoney';
import { ListGuestIcon } from '@zola/zola-ui/src/components/SvgIconsV3/ListGuest';
import { PaperInvitationsIcon } from '@zola/zola-ui/src/components/SvgIconsV3/PaperInvitations';
import { GiftIcon } from '@zola/zola-ui/src/components/SvgIconsV3/Gift';
import { PaperPlaceCardIcon } from '@zola/zola-ui/src/components/SvgIconsV3/PaperPlaceCard';
import { PhotoAlbumIcon } from '@zola/zola-ui/src/components/SvgIconsV3/PhotoAlbum';
import { DeviceDesktopIcon } from '@zola/zola-ui/src/components/SvgIconsV3/DeviceDesktop';

// Constants
import { PrimaryNavIdentifier } from '@zola-helpers/client/dist/es/constants/navConstants';

// Types
import { BusinessUnitComponent } from '@zola-helpers/client/dist/es/@types';
import type { UserContext } from '@zola-helpers/client/dist/es/@types';

// Utils
import featureFlags from 'util/featureFlags';
import { getHomestoreHref } from 'util/getHomestoreHref';
import experimentFlags, { CORE_VERTICAL_NAV_REORDERING } from 'util/experimentFlags';

export type VerticalNavData = {
  activeNavType?: PrimaryNavIdentifier;
  businessUnitComponent?: BusinessUnitComponent; // TODO RYK: tracking TBD
  icon?: ReactNode;
  isProductActivated?: boolean;
  name: string;
  href: string;
  tooltipText?: string;
};

export const getPrimaryLinks = (userContext: UserContext, iconSize = 24): VerticalNavData[] => {
  const {
    budget,
    has_guest_list,
    has_invitation_account,
    has_marketplace_account,
    has_registry,
    has_website,
    invitation_account,
    slug,
    wedding_account,
  } = userContext;

  const yourWedding = {
    name: 'Your wedding',
    href: '/wedding/manage/v2',
    activeNavType: PrimaryNavIdentifier.YOUR_WEDDING,
    icon: <HomeIcon title="Your wedding" width={iconSize} height={iconSize} />,
  };

  const vendorMarketplace = {
    name: 'Venue and vendors',
    href: '/wedding-vendors',
    activeNavType: PrimaryNavIdentifier.VENDORS,
    icon: <VenueIcon title="Venue and vendors" width={iconSize} height={iconSize} />,
    isProductActivated: has_marketplace_account,
    tooltipText: 'Discover, reach out to, and book vendors who are right for you.',
  };

  const website = {
    name: 'Website',
    href: has_website ? '/wedding/manage/website/manage' : '/wedding-planning/website/designs',
    activeNavType: PrimaryNavIdentifier.WEBSITE,
    icon: <DeviceDesktopIcon title="Website" width={iconSize} height={iconSize} />,
    isProductActivated: has_website,
    tooltipText: 'Customize a design to share wedding details, updates, FAQs, and more.',
  };

  const budgetTool = {
    name: 'Budget tool',
    href: '/wedding/manage/budget',
    activeNavType: PrimaryNavIdentifier.BUDGET,
    icon: <ReportMoneyIcon title="Budget tool" width={iconSize} height={iconSize} />,
    isProductActivated: Boolean(budget?.uuid),
    tooltipText: 'Track spending, set payment reminders, and get helpful tips.',
  };

  const guestList = {
    name: 'Guest list and RSVPs',
    href: '/wedding/manage/guests/all',
    activeNavType: PrimaryNavIdentifier.GUEST_LIST,
    icon: <ListGuestIcon title="Guest list and RSVPs" width={iconSize} height={iconSize} />,
    isProductActivated: has_guest_list,
    tooltipText: "Add names, get addresses, and track who's coming.",
  };

  const invitesAndPaper = {
    name: 'Invites and paper',
    href: has_invitation_account
      ? '/wedding-planning/projects/drafts'
      : '/wedding-planning/universal',
    activeNavType: PrimaryNavIdentifier.PAPER,
    icon: <PaperInvitationsIcon title="Invites and paper" width={iconSize} height={iconSize} />,
    isProductActivated: has_invitation_account,
    tooltipText: 'Customize 1000+ beautiful designs for every need.',
  };

  const registry = {
    name: 'Registry',
    href: has_registry ? `/registry/${slug}/edit` : '/onboard/new',
    activeNavType: PrimaryNavIdentifier.REGISTRY,
    icon: <GiftIcon title="Registry" width={iconSize} height={iconSize} />,
    isProductActivated: has_registry,
    tooltipText: 'Add gifts, experiences, and cash funds, plus track who gave you what.',
  };

  const seatingChart = {
    name: 'Seating chart',
    href: '/wedding/manage/seating',
    activeNavType: PrimaryNavIdentifier.SEATING,
    icon: <PaperPlaceCardIcon title="Seating chart" width={iconSize} height={iconSize} />,
    isProductActivated: wedding_account?.seating_chart_activated,
    tooltipText: 'Place guests at their tables for every event.',
  };
  const albums = {
    name: 'Albums',
    href: invitation_account?.photobooks_onboarding_completed
      ? '/wedding-albums/drafts'
      : '/wedding-albums/shop',
    activeNavType: PrimaryNavIdentifier.ALBUMS,
    icon: <PhotoAlbumIcon title="Albums" width={iconSize} height={iconSize} />,
    isProductActivated: invitation_account?.photobooks_onboarding_completed,
    tooltipText: 'Create a stunning keepsake the easy way.',
  };

  if (experimentFlags.isUnderTest(CORE_VERTICAL_NAV_REORDERING, userContext)) {
    return [
      yourWedding,
      vendorMarketplace,
      website,
      registry,
      invitesAndPaper,
      budgetTool,
      guestList,
      seatingChart,
      albums,
    ];
  }

  return [
    yourWedding,
    vendorMarketplace,
    website,
    budgetTool,
    guestList,
    invitesAndPaper,
    registry,
    seatingChart,
    albums,
  ];
};

export const getSecondaryLinks = (): VerticalNavData[] => {
  return [
    { name: 'Community', href: '/community', activeNavType: PrimaryNavIdentifier.COMMUNITY },
    {
      name: 'Expert advice',
      href: '/expert-advice',
      activeNavType: PrimaryNavIdentifier.ADVICE,
    },
    {
      name: 'Checklist',
      href: '/wedding-planning/checklist',
      activeNavType: PrimaryNavIdentifier.CHECKLIST,
    },
    {
      name: 'Inspiration',
      href: '/inspiration',
      activeNavType: PrimaryNavIdentifier.REAL_WEDDINGS,
    },
    {
      name: 'Boutique',
      href: '/shop/category/wedding-shop',
      activeNavType: PrimaryNavIdentifier.WEDDING_BOUTIQUE,
    },
    { name: 'Zola Home', href: getHomestoreHref(featureFlags.getEnvironment()) },
    { name: 'Help', href: '/contact-us', activeNavType: PrimaryNavIdentifier.HELP },
  ];
};
